import { Color, Vector3 } from 'three/webgpu';
import { uniform } from 'three/tsl';

/**
 * @file Global TSL uniforms — ONE object per value, shared by every material,
 * so a single write per frame updates the whole world.
 *
 * These are TSL `uniform()` node objects (write `uNight.value = x`), not plain
 * `{value}` bags: they can be used directly inside node graphs.
 *
 * Who writes what:
 *   uNight, uWarm, uFogColor, uFogDensity  -> systems/DayNight.js (T2)
 *   uTime                                  -> Engine render tick (T0)
 *   uPlayerPos                             -> actors/Player.js (T5)
 *   uOvercast, uRainAmt, uLightning        -> systems/Weather.js
 *
 * ⚠ THE THREE WEATHER UNIFORMS DEFAULT TO EXACTLY 0, AND THAT IS A CONTRACT.
 * `render/nodes.js#weatherGrade` is folded into `dayNightVertexColor()`, i.e.
 * into EVERY baked material in the project. At 0 its maths is an exact
 * identity — `mix(c, grey, 0) === c`, `c * mix(1, k, 0) === c`, `c + t*0 === c`
 * — so a scene with no Weather system renders byte-for-byte what it always did.
 * `tests/weather.test.mjs` asserts the defaults; keep them zero.
 */

/** 0 = full day, 1 = full night. Blends `vertexColor(0)` -> `vertexColor(1)`. */
export const uNight = uniform(0);

/** 0 away from the horizon, 1 in the golden band. Gold-grades the DAY channel
 *  only, so dawn/dusk needs no third bake. */
export const uWarm = uniform(0);

/** Elapsed scaled seconds. Drives sway, flicker and sprite animation. */
export const uTime = uniform(0);

// ── the moving sun ───────────────────────────────────────────────────────────
// Written by `systems/DayNight.js` from `bake/sunArc.js`. Only materials built
// with `{sun: true}` read these — see the gate note in `render/nodes.js`.

/**
 * Live sun direction (surface -> sun), unit length. Feeds the `dot(n, L)` term
 * that replaced the baked one.
 *
 * ⚠ This is now THE sun, not a decoration. Before visibility keys existed,
 * `DayNight` owned a purely visual sun that was explicitly forbidden from
 * touching shading; that restriction is gone precisely because this uniform and
 * the sky disc are now derived from the same `sunArc` call.
 */
export const uSunDir = uniform(new Vector3(0.42, 0.67, 0.61).normalize());

/**
 * Blend between the two RESIDENT visibility keys: 0 = key `a`, 1 = key `b`.
 * `bake/sunKeys.js` keeps only the pair straddling the current hour on the GPU,
 * so this never needs to know which two they are.
 */
export const uSunMix = uniform(0);

/** Scene fog colour; matched to the sky's horizon stop by DayNight. */
export const uFogColor = uniform(new Color(0x0a0a0f));

/** Exponential fog density (per metre). Forest doubles it. */
export const uFogDensity = uniform(0.0);

/** World-space player position. Used for proximity effects (grass push-away,
 *  torch pools, interior fade). */
export const uPlayerPos = uniform(new Vector3());

// ── weather ──────────────────────────────────────────────────────────────────
// All three are DAMPED ramps written by `systems/Weather.js`; nothing else may
// write them. Read the contract note at the top of this file before changing a
// default.

/** 0 = clear sky, 1 = full cloud. Desaturates, darkens and cools the whole
 *  world through {@link import('../render/nodes.js').weatherGrade} — applied
 *  AFTER the day/night blend, so it works at noon and at midnight alike. Also
 *  greys the sky stops and the fog, and kills the ocean glint. */
export const uOvercast = uniform(0);

/** 0 = dry, 1 = a storm's worth of rain. Drives `render/weather.js`'s streak
 *  density/length/alpha and the ocean's chop. NOT its fall SPEED — see the
 *  note in `render/weather.js`, a speed that ramps shears the fall phase. */
export const uRainAmt = uniform(0);

/** Lightning flash, 0 normally, spiked to 1 and exp-decayed over ~0.25 s by the
 *  storm scheduler. Lifts the whole scene toward a bright cool white and lifts
 *  the sky dome with it — there is no bolt geometry, the global lift IS the
 *  read at this camera pitch. */
export const uLightning = uniform(0);
